import React, { Fragment, useState, useEffect } from "react";
import { Col, Card, Table, Modal, Button, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import Snackbars from "../../../utils/Snackbars";
import useRequests from "../../../utils/useRequests";
import * as Yup from "yup";
import { useFormik } from "formik";

const ResellSettings = () => {
  const location = useLocation();
  const { getRequest, putRequest } = useRequests();
  const [settingsData, setSettingsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);

  const [AlertData, setAlertData] = useState({
    variant: "",
    message: "",
  });

  const resetAlertData = () => {
    setTimeout(() => {
      setAlertData({ variant: "", message: "" });
    }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
  };

  // Fetch settings data
  const fetchSettings = async () => {
    setLoading(true);
    const response = await getRequest("site-settings");
    if (response && response.code === 200) {
        let newdata = [];
        response.data.forEach(element => {
            if(element.ss_id== 5 || element.ss_id== 6){
                newdata.push(element);
            }
        });
      setSettingsData(newdata);
    } else {
      setAlertData({ variant: "error", message: "Failed to fetch settings" });
      resetAlertData();
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSettings();
    if (location.state?.message) {
      setAlertData({ variant: location.state.status, message: location.state.message });
      resetAlertData();
    }
  }, []);

  // **Formik Instance with Dynamic Values**
  const validationSchema = Yup.object({
    setting_name: Yup.string().required("Title is required"),
    setting_value: Yup.string().required("Setting value is required"),
  });

  const formik = useFormik({
    initialValues: {
      setting_name: "",
      setting_value: "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (currentEdit?.id) {
        const jsonpar = JSON.stringify(values);
        const response = await putRequest(`site-edit/${currentEdit.id}`, jsonpar);

        if (response && response.code === 200) {
          setSettingsData((prev) =>
            prev.map((item) =>
              item.ss_id === currentEdit.id
                ? { ...item, setting_value: values.setting_value }
                : item
            )
          );
          setAlertData({ variant: "success", message: "Settings updated successfully!" });
          resetAlertData();
          handleModalClose();
        } else {
          setAlertData({ variant: "error", message: "Failed to update setting." });
          resetAlertData();
        }
      }
    },
  });

  // **Update Formik Values When Editing**
  useEffect(() => {
    if (currentEdit) {
      formik.setValues({
        setting_name: currentEdit.setting_name || "",
        setting_value: currentEdit.setting_value || "",
      });
    }
  }, [currentEdit]);

  // **Handle Edit Click**
  const handleEditClick = async (item) => {
    setModalShow(true);
    setCurrentEdit({
      id: item.ss_id,
      setting_name: item.setting_name,
      setting_value: item.setting_value,
    });

    const response = await getRequest(`site/${item.ss_id}`);
    if (response && response.code === 200) {
      setCurrentEdit({
        id: response.data[0].ss_id,
        setting_name: response.data[0].setting_name,
        setting_value: response.data[0].setting_value,
      });
    } else {
      setAlertData({ variant: "error", message: "Failed to fetch settings data for editing." });
      resetAlertData();
    }
  };

  // **Handle Modal Close**
  const handleModalClose = () => {
    setModalShow(false);
    setCurrentEdit(null);
  };

  return (
    <Fragment>
      <div className="d-sm-flex d-block justify-content-between align-items-center mb-4">
        <h2 className="text-black font-w600 mb-0">Resell Ticket Settings</h2>
        {AlertData.message && <Snackbars variant={AlertData.variant} message={AlertData.message} />}
      </div>

      <Col lg={12}>
        <Card>
          <Card.Body>
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" />
                <p>Loading settings...</p>
              </div>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Value</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {settingsData.map((item) => (
                    <tr key={item.ss_id}>
                      <td>{item.setting_name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</td>
                      <td>{item.setting_value+"%"}</td>
                      <td className="text-center">
                        <Button onClick={() => handleEditClick(item)} className="btn btn-primary btn-xs sharp" title="Edit Setting">
                          <i className="flaticon-381-edit-1"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* **Modal for Editing** */}
      <Modal show={modalShow} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Resell Ticket Setting</Modal.Title>
        </Modal.Header>
        <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
         
            <div className="form-group mb-3">
              <label>Title:</label>
              <input
                type="text"
                className="form-control"
                readOnly
                name="setting_name"
                value={formik.values.setting_name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              />
            </div>
            <div className="form-group mb-3">

              <label>Value:</label>
              <div className="input-group">
                <input
                  type="number"
                  min="0"
                  max="50"
                  className={`form-control ${
                    formik.touched.setting_value && formik.errors.setting_value ? "is-invalid" : ""
                  }`}
                  name="setting_value"
                  value={formik.values.setting_value}
                  onChange={formik.handleChange}
                />
                <div className="input-group-append">
                  <span className="input-group-text" style={{ padding: '12px 20px', borderRadius: '0 7px 7px 0', marginLeft: '0'}}>%</span>
                </div>
                {formik.touched.setting_value && formik.errors.setting_value && (
                <div className="invalid-feedback">{formik.errors.setting_value}</div>
                )}
              </div>
     
            </div>

        </Modal.Body>
        <Modal.Footer>
              <Button variant="light" onClick={handleModalClose}>
                Close
              </Button>
              <Button variant="primary" type="submit">
                Update
              </Button>
            </Modal.Footer>
            </form>

      </Modal>
    </Fragment>
  );
};

export default ResellSettings;
